export const productCostsTableHeaders = {
    image: {
        label: "Image",
        value: "image"
    },
    productName: {
        label: "Product Name",
        value: "productName",
        className: "w-[450px] min-w-[350px]"
    },
    sku: {
        label: "SKU",
        value: "sku"
    },
    productBarcode: {
        label: "Barcode",
        value: "productBarcode"
    },
    location: {
        label: "Location",
        value: "location"
    },
    unitCost: {
        label: "Unit Cost",
        value: "unitCost",
        tooltip: `
<div class='text-left leading-5'>
  <div><b>Unit Cost</b> (per unit)</div>
  <ul style="margin:6px 0 0 16px; padding:0; list-style:disc;">
    <li><b>Default</b>: taken from the <b>latest PO</b> for this SKU (prefers POs with received boxes/containers).</li>
    <li><b>BOM add-on</b>: the backend adds any connected BOM unit-cost rollup via <code>getLineItemPrice(lineItemPrice + sumConnectedBOM)</code>.</li>
    <li><b>If no suitable PO</b>: falls back to the BOM rollup (<code>sumConnectedBOM</code>), otherwise <code>0</code>.</li>
    <li><b>Kit</b>: sums Unit Cost across kit components that have <b>received boxes/containers</b>.</li>
    <li><b>Manual-kit / source SKUs</b>: may sum across <code>source_skus</code> depending on product flags (see backend rules).</li>
  </ul>
</div>
        `.trim()
    },
    // packagingCost: {
    //     label: "Packaging",
    //     value: "packagingCost"
    // },
    shippingCost: {
        label: "Shipping Each",
        value: "shippingCost",
        className: "min-w-[135px]",
        tooltip: `
<div class='text-left leading-5'>
  <div><b>Shipping Each</b> (per unit)</div>
  <div style="margin-top:6px;"><b>How it's calculated (backend)</b>:</div>
  <ul style="margin:6px 0 0 16px; padding:0; list-style:disc;">
    <li>Pick the <b>latest PO</b> for this SKU (prefers POs with received boxes/containers).</li>
    <li>Compute <b>total shipping payments</b> on that PO:
      <div style="margin-top:4px;"><code>shippingTotal = sum(Payments where type === SHIPPING).amount</code></div>
    </li>
    <li>Compute <b>total quantity ordered on the PO</b> (sum of quantities across ALL line items on the PO):
      <div style="margin-top:4px;"><code>poQtyTotal = Σ(lineItems.quantity for PO)</code></div>
    </li>
    <li>Distribute shipping across the PO units:
      <div style="margin-top:4px;"><code>shippingEach = shippingTotal / poQtyTotal</code></div>
    </li>
    <li>If there are <b>no received boxes/containers</b> for that PO/SKU, backend returns <code>null</code> / <code>N/A</code>.</li>
    <li><b>Kits</b>: sums Shipping Each across kit components that have received boxes/containers.</li>
    <li><b>Source SKUs</b> (some Amazon/manual-kit cases): sums Shipping Each across <code>source_skus</code>.</li>
  </ul>
</div>
        `.trim()
    },
    tariffCost: {
        label: "Tariff Each",
        value: "tariffCost",
        className: "min-w-[115px]",
        tooltip: `
<div class='text-left leading-5'>
  <div><b>Tariff Each</b> (per unit)</div>
  <div style="margin-top:6px;"><b>How it's calculated (backend)</b>:</div>
  <ul style="margin:6px 0 0 16px; padding:0; list-style:disc;">
    <li>Pick the <b>latest PO</b> for this SKU (prefers POs with received boxes/containers).</li>
    <li>Compute <b>total tariff payments</b> on that PO:
      <div style="margin-top:4px;"><code>tariffTotal = sum(Payments where type === TARIFF).amount</code></div>
    </li>
    <li>Compute <b>total PO quantity</b> across ALL line items:
      <div style="margin-top:4px;"><code>poQtyTotal = Σ(lineItems.quantity for PO)</code></div>
    </li>
    <li>Distribute tariff across PO units:
      <div style="margin-top:4px;"><code>tariffEach = tariffTotal / poQtyTotal</code></div>
    </li>
    <li><b>Kits</b>: sums Tariff Each across received kit components.</li>
    <li><b>Source SKUs</b>: sums Tariff Each across <code>source_skus</code> in specific backend cases.</li>
  </ul>
</div>
        `.trim()
    },
    totalLandedCost: {
        label: "Landed Cost",
        value: "totalLandedCost",
        className: "min-w-[140px]",
        tooltip: `
<div class='text-left leading-5'>
  <div><b>Landed Cost</b> (per unit)</div>
  <div style="margin-top:6px;"><b>How it's calculated (backend)</b>:</div>
  <ul style="margin:6px 0 0 16px; padding:0; list-style:disc;">
    <li>Compute per-unit shipping/tariff from PO payments as described in <b>Shipping Each</b> / <b>Tariff Each</b>.</li>
    <li>Compute per-unit item cost (Unit Cost + BOM rollup when applicable):
      <div style="margin-top:4px;"><code>itemEach = getLineItemPrice(lineItemPrice + sumConnectedBOM)</code></div>
    </li>
    <li>Then:
      <div style="margin-top:4px;"><code>landedEach = itemEach + shippingEach + tariffEach</code></div>
    </li>
    <li>If there are no received boxes/containers, landed cost is not considered valid → <code>N/A</code>.</li>
    <li><b>Kits</b>: sums Landed Cost across kit components that have received boxes/containers.</li>
    <li><b>Source SKUs</b>: in specific backend cases, sums Landed Cost across <code>source_skus</code>.</li>
  </ul>
</div>
        `.trim()
    },
    weightedCost: {
        label: "Weighted Cost",
        value: "weightedCost",
        className: "min-w-[145px]",
        tooltip: `
<div class='text-left leading-5'>
  <div><b>Weighted Cost</b> (per unit)</div>
  <div style="margin-top:6px;"><b>How it's calculated (backend)</b>:</div>
  <ul style="margin:6px 0 0 16px; padding:0; list-style:disc;">
    <li>Looks at up to the <b>2 most recent POs</b> where this SKU has received boxes/containers.</li>
    <li>For each PO, computes <code>landedEach</code> using:
      <div style="margin-top:4px;"><code>landedEach = itemEach + (shippingTotal/poQtyTotal) + (tariffTotal/poQtyTotal)</code></div>
    </li>
    <li>Weights by this SKU’s quantity on that PO:
      <div style="margin-top:4px;"><code>weighted = Σ(landedEach_po × skuQty_po) / Σ(skuQty_po)</code></div>
    </li>
    <li>If there are no received boxes/containers, weighted cost is <code>0</code> / <code>N/A</code>.</li>
    <li><b>Kits</b>: sums Weighted Cost across kit components that have received boxes/containers.</li>
  </ul>
</div>
        `.trim()
    },
    // variance: {
    //     label: "Variance",
    //     value: "variance"
    // },
    onHand: {
        label: "On Hand",
        value: "onHand"
    },
    onOrder: {
        label: "On Order",
        value: "onOrder"
    },
    inventoryValue: {
        label: "Inventory Value",
        value: "inventoryValue",
        className: "min-w-[160px]",
        tooltip: `
<div class='text-left leading-5'>
  <div><b>Inventory Value</b></div>
  <ul style="margin:6px 0 0 16px; padding:0; list-style:disc;">
    <li><code>inventoryValue = onHand × landedCost</code></li>
    <li><b>Landed Cost</b> is the per-unit landed cost described above.</li>
    <li>The header total is the sum of Inventory Value across the visible rows.</li>
  </ul>
</div>
        `.trim()
    },
    hasGroup: {
        label: "Grouped",
        value: "hasGroup",
        className: "min-w-[100px]"
    }
}

export const productCostsTableHiddenHeaders = [
    "sku",
    "location",
]